import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import atributoService, { AtributoValor, OrigenAtributo } from '../services/atributoService';

const normalizar = (valor: string): string => (valor ?? '').trim().toUpperCase();

const ORIGEN_LABEL: Record<OrigenAtributo, { singular: string; plural: string }> = {
  cliente: { singular: 'cliente', plural: 'clientes' },
  articulo: { singular: 'artículo', plural: 'artículos' },
};

interface AtributoSearchProps {
  origen: OrigenAtributo;
  /** Valor elegido por posición: [atributo 1, atributo 2]. */
  seleccion: [string, string];
  disabled?: boolean;
  onChange: (slot: 0 | 1, value: string) => void;
}

const AtributoSearch: React.FC<AtributoSearchProps> = ({ origen, seleccion, disabled, onChange }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<AtributoValor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const label = ORIGEN_LABEL[origen];

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await atributoService.getAtributosOferta(origen, input);
        if (cancelado) return;
        setOptions(items);
        setError(false);
      } catch {
        if (cancelado) return;
        setOptions([]);
        setError(true);
      } finally {
        if (!cancelado) setLoading(false);
      }
    }, 300);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [input, open, origen]);

  const elegidos = seleccion
    .map((valor, i) => ({ slot: i as 0 | 1, valor: (valor ?? '').trim() }))
    .filter((a) => a.valor);

  /** El mismo valor no puede quedar en la posición 1 y 2 a la vez. */
  const elegidosNormalizados = new Set(elegidos.map((a) => normalizar(a.valor)));
  const opcionesDisponibles = options.filter((o) => !elegidosNormalizados.has(normalizar(o.valor)));

  return (
    <Box>
      <Autocomplete<AtributoValor>
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={opcionesDisponibles}
        loading={loading}
        disabled={disabled}
        value={null}
        inputValue={input}
        onInputChange={(_, v, reason) => {
          if (reason !== 'reset') setInput(v);
        }}
        onChange={(_, opcion) => {
          if (!opcion) return;
          onChange((opcion.posicion - 1) as 0 | 1, opcion.valor);
          setInput('');
        }}
        filterOptions={(x) => x}
        getOptionLabel={(o) => o.valor}
        isOptionEqualToValue={(a, b) => a.posicion === b.posicion && a.valor === b.valor}
        noOptionsText={
          error
            ? 'No se pudieron cargar los atributos.'
            : input.trim() || options.length
              ? 'No hay atributos que coincidan.'
              : `No hay atributos cargados en ${label.plural}.`
        }
        renderOption={(props, o) => (
          <li {...props} key={`${o.posicion}-${o.valor}`}>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', gap: 2 }}>
              <span>
                Atributo {o.posicion}: <strong>{o.valor}</strong>
              </span>
              <Typography variant="caption" color="text.secondary">
                {o.cantidad} {o.cantidad === 1 ? label.singular : label.plural}
              </Typography>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Buscar atributo de ${label.singular}`}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.5 }}>
        La oferta aplica a los {label.plural} que tengan todos los atributos elegidos, en la misma posición.
      </Typography>
      {elegidos.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {elegidos.map(({ slot, valor }) => (
            <Chip
              key={slot}
              label={`Atributo ${slot + 1}: ${valor}`}
              color="primary"
              size="small"
              onDelete={disabled ? undefined : () => onChange(slot, '')}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default AtributoSearch;
